import AffiliatePage from '@/page/AffiliatePage'
import AffiliateLinksPage from '@/page/affiliate/AffiliateLinksPage'
import AffiliateHelpPage from '@/page/affiliate/AffiliateHelpPage'
import AffiliateWelcomePage from '@/page/affiliate/AffiliateWelcomePage'
import AffiliatePayoutPage from '@/page/affiliate/AffiliatePayoutPage'
import AffiliateQrcodePage from '@/page/affiliate/AffiliateQrcodePage'
import AffiliatePhotosPage from '@/page/affiliate/AffiliatePhotosPage'
import AffiliateVideosPage from '@/page/affiliate/AffiliateVideosPage'

/*
 * Affiliate routing conventions (read this before adding new affiliate routes):
 *
 * 1. Section components live under `@/components/affiliate/<section>/` — NOT
 *    under `@/page/affiliate/`. The `@/page/affiliate/*Page.vue` imports above
 *    are legacy and being phased out as their sections are rebuilt. New
 *    sections (e.g. customer/, creator/, reports/) own their components,
 *    including the dashboard tile (`Affiliate<Section>Summary.vue`) which now
 *    lives in the section folder, not in the deprecated
 *    `@/components/affiliate/summary/` folder.
 *
 * 2. Use eager top-of-file imports for every route component (see existing
 *    `import Affiliate... from '@/components/affiliate/.../Affiliate...'`
 *    lines below). Do NOT use lazy `() => import(...)` syntax — the bundle
 *    is shipped as a single chunk by current vue.config; lazy syntax breaks
 *    the consistency check and gains us nothing today. If/when we adopt
 *    code-splitting, that's a deliberate repo-wide change and this comment
 *    should be updated at the same time.
 *
 * 3. `meta.primaryNavigation` controls which bottom-nav tab is highlighted.
 *    Use `'affiliate'` for sub-pages of the dashboard, or a dedicated key
 *    if you also add a new top-level bottom-nav item in
 *    `@/components/navigation/PrimaryNavigation.vue`.
 */

import AffiliatePromoteIndex from '@/components/affiliate/promote/AffiliatePromoteIndex'
import AffiliateBonusIndex from '@/components/affiliate/bonus/AffiliateBonusIndex'

import AffiliateCustomerIndex from '@/components/affiliate/customer/AffiliateCustomerIndex'
import AffiliateCustomerView from '@/components/affiliate/customer/AffiliateCustomerView'

import AffiliateCreatorIndex from '@/components/affiliate/creator/AffiliateCreatorIndex'
import AffiliateCreatorView from '@/components/affiliate/creator/AffiliateCreatorView'

import AffiliateCreditShareIndex from '@/components/affiliate/userCreditShare/AffiliateCreditShareIndex'
import AffiliateCreditShareView from '@/components/affiliate/userCreditShare/AffiliateCreditShareView'

import AffiliateReserveIndex from '@/components/affiliate/reserve/AffiliateReserveIndex'

import AffiliateDiscountIndex from '@/components/affiliate/discount/AffiliateDiscountIndex'

import AffiliateReportsIndex from '@/components/affiliate/reports/AffiliateReportsIndex'
import AffiliateReportsSales from '@/components/affiliate/reports/AffiliateReportsSales'
import AffiliateReportsLanding from '@/components/affiliate/reports/AffiliateReportsLanding'

const routes = [
    {
        path: '/affiliate/discount/index',
        component: AffiliateDiscountIndex,
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/creator/index',
        component: AffiliateCreatorIndex,
        name: 'affiliateCreatorIndex',
        meta: { primaryNavigation: 'affiliateCreator' },
    },
    {
        path: '/affiliate/creator/view/:id',
        component: AffiliateCreatorView,
        name: 'affiliateCreatorView',
        meta: { primaryNavigation: 'affiliateCreator' },
    },
    {
        path: '/affiliate/customer/index',
        component: AffiliateCustomerIndex,
        name: 'affiliateCustomers',
        meta: { primaryNavigation: 'customer' },
    },
    {
        path: '/affiliate/customer/view/:id',
        component: AffiliateCustomerView,
        name: 'affiliateCustomerView',
        meta: { primaryNavigation: 'customer' },
    },
    {
        path: '/affiliate',
        component: AffiliatePage,
        name: 'affiliateDashboard',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/reports',
        component: AffiliateReportsIndex,
        name: 'affiliateReports',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/reports/sales',
        component: AffiliateReportsSales,
        name: 'affiliateReportsSales',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/reports/landing',
        component: AffiliateReportsLanding,
        name: 'affiliateReportsLanding',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/links',
        component: AffiliateLinksPage,
        name: 'affiliateLinks',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/promote',
        component: AffiliatePromoteIndex,
        name: 'affiliatePromote',
        meta: { primaryNavigation: 'promote' },
    },
    {
        path: '/affiliate/bonus',
        component: AffiliateBonusIndex,
        name: 'affiliateBonus',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/userCreditShare',
        component: AffiliateCreditShareIndex,
        name: 'affiliateCreditShare',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/userCreditShare/:id',
        component: AffiliateCreditShareView,
        name: 'affiliateCreditShareView',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/help',
        component: AffiliateHelpPage,
        name: 'affiliateHelp',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/welcome',
        component: AffiliateWelcomePage,
        name: 'affiliateWelcome',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/payouts',
        component: AffiliatePayoutPage,
        name: 'affiliatePayout',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/qrcode',
        component: AffiliateQrcodePage,
        name: 'affiliateQrcode',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/photos',
        component: AffiliatePhotosPage,
        name: 'affiliatePhotos',
        meta: { primaryNavigation: 'affiliate' },
    },
    {
        path: '/affiliate/videos',
        component: AffiliateVideosPage,
        name: 'affiliateVideos',
        meta: { primaryNavigation: 'affiliate' },
    },
    // defunct
    {
        path: '/affiliate/reserve/index',
        component: AffiliateReserveIndex,
        name: 'affiliateReserveIndex',
        meta: { primaryNavigation: 'affiliate' },
    },
];

export default routes;
